/**
 * "Continue with Google" (ENG-6165): Google's own button, rendered by Google
 * Identity Services as its brand rules require. Nothing shows until the
 * library loads, and nothing at all if it fails: the form still works.
 * SocialSignInButtons draws the divider once the button shows.
 */
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import {
  type GoogleButtonConfiguration,
  type GoogleId,
  loadGoogleIdentity,
} from '@/auth/utils/googleIdentity';
import config from '@/config';

// Google's button takes a pixel width between 200 and 400.
const MIN_WIDTH = 200;
const MAX_WIDTH = 400;

export interface GoogleSignInButtonProps {
  text: 'continue_with' | 'signup_with';
  /** Receives the Google ID token. A credential: never log it. */
  onCredential: (idToken: string) => void;
  /** Greys the button out and drops any credential that arrives meanwhile. */
  disabled?: boolean;
  /** The Google OAuth client id; a seam for stories and tests. */
  clientId?: string;
  /** Loads the library; a seam for stories and tests. */
  load?: () => Promise<GoogleId>;
  /** Told when Google's button appears and goes. */
  onReadyChange?: (ready: boolean) => void;
}

function buttonWidth(el: HTMLElement): number {
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, el.clientWidth || MAX_WIDTH));
}

export default function GoogleSignInButton({
  text,
  onCredential,
  disabled = false,
  clientId = config.googleClientId,
  load = loadGoogleIdentity,
  onReadyChange,
}: GoogleSignInButtonProps): JSX.Element {
  const container = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const readyHandler = useRef(onReadyChange);
  useEffect(() => {
    readyHandler.current = onReadyChange;
  }, [onReadyChange]);
  useEffect(() => {
    readyHandler.current?.(ready);
  }, [ready]);
  // Google keeps the callback from the last initialize; this ref lets it reach
  // the current handler without initializing again on every render.
  const credentialHandler = useRef(onCredential);
  const isDisabled = useRef(disabled);
  useEffect(() => {
    credentialHandler.current = onCredential;
    isDisabled.current = disabled;
  }, [onCredential, disabled]);

  useEffect(() => {
    const el = container.current;
    if (!clientId || !el) return undefined;
    let cancelled = false;
    load()
      .then((google) => {
        if (cancelled) return;
        // initialize again on every mount: the last call's callback wins, and
        // this page's is the one that must receive the credential.
        google.initialize({
          client_id: clientId,
          // Google keeps this callback past an unmount: a popup that finishes
          // after the page moved on, or while it is busy, delivers nothing.
          callback: ({ credential }) => {
            if (!cancelled && !isDisabled.current) credentialHandler.current(credential);
          },
        });
        const options: GoogleButtonConfiguration = {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text,
          shape: 'rectangular',
          logo_alignment: 'center',
          width: buttonWidth(el),
          // The app is English-only; Google would follow the browser locale.
          locale: 'en',
        };
        google.renderButton(el, options);
        setReady(true);
      })
      .catch(() => {
        // No Google button; the form below is the way in.
      });
    return () => {
      cancelled = true;
      // Google's markup is not React's: clear it so a remount renders once.
      el.replaceChildren();
      setReady(false);
    };
  }, [clientId, load, text]);

  return (
    <div
      ref={container}
      className={clsx('flex justify-center', disabled && 'pointer-events-none opacity-50')}
      aria-disabled={disabled || undefined}
      data-testid="google-sign-in-button"
    />
  );
}
